import React, { useEffect, useRef, useState } from 'react';
import * as go from 'gojs';
import { ReactDiagram, ReactOverview, ReactPalette } from 'gojs-react';
import {
  EyeOutlined,
  EditOutlined,
  FontSizeOutlined,
  SaveOutlined,
  ImportOutlined,
  DeleteOutlined,
  ZoomInOutlined,
  ZoomOutOutlined,
  ExpandOutlined,
  ExportOutlined,
  EnvironmentOutlined,
} from '@ant-design/icons';
import GuidedDraggingTool from './extensions/GuidedDraggingTool';
import LinkLabelOnPathDraggingTool from './extensions/LinkLabelOnPathDraggingTool';
import paletteConfig from './palette.json';
import PointInfoDrawer from './PointInfoDrawer';
import type { PointInfo } from './PointInfoDrawer';
import { message } from 'antd';
import TopBar from '@/Components/TopBar';
import Navbar from "@/Components/navbar";
import userInfo from '@/Components/user';
import { useParams, useNavigate } from 'react-router-dom';
import { getFlow, saveFlow, flows } from '@/services/common';
import { updateFlowthum_Pic } from '@/config/func';
import './index.css';

const $ = go.GraphObject.make;

interface PaletteChild {
  name: string;
  type: string;
  icon: string;
}

interface PaletteCategory {
  name: string;
  type: string;
  children: PaletteChild[];
}

const categories = paletteConfig as PaletteCategory[];

/** 文本框样式（存于节点数据，随 JSON 保存） */
interface TextStyleForm {
  fontSize: number;
  textColor: string;
  bold: boolean;
  italic: boolean;
  bgColor: string;
  borderColor: string;
  borderWidth: number;
  /** 圆角半径（文本框/名称标签底框 RoundedRectangle 的 parameter1） */
  borderRadius?: number;
  textMargin: number;
}

/** 连线样式（存于连线数据，随 JSON 保存） */
interface LinkStyleForm {
  lineDash: 'solid' | 'dash';
  dashLength: number;
  dashGap: number;
  lineWidth: number;
  lineColor: string;
  arrowSize: number;
  /** 是否隐藏箭头（true 时该连线的箭头不显示） */
  arrowHidden: boolean;
}

const iconBase = `${process.env.PUBLIC_URL}/icons/`;

/** 本页面读写的项目（实时监控固定为该项目），流程 id 由路由参数 :f 决定 */
const OBSERVATION_PROJECT_ID = 154;

/** 作为快捷入口的主流程：放在快捷入口栏的第一位 */
const MONITOR_ENTRY_FLOW_ID = 615;
/** 不显示在监控列表、只作为快捷入口的流程 id */
const MONITOR_SUB_FLOW_IDS = [618, 619, 620, 621, 622, 623, 624];
/** 需要展示快捷入口栏的流程 id（主流程 + 子流程，主流程在前） */
const MONITOR_ENTRY_FLOW_IDS = [MONITOR_ENTRY_FLOW_ID, ...MONITOR_SUB_FLOW_IDS];

/** 悬停时显示 / 隐藏连接端口（元件库面板中不显示，只读模式下始终不显示） */
function setPortsVisible(node: go.Node, visible: boolean): void {
  if (node.diagram instanceof go.Palette) return;
  if (node.diagram?.isReadOnly) visible = false;
  node.ports.each((p) => {
    if (p instanceof go.Shape && p.portId) {
      p.fill = visible ? '#4a90d9' : 'transparent';
      p.stroke = visible ? '#ffffff' : null;
    }
  });
}

/**
 * 在元件一条边上创建一个连接端口。
 * 每个方向 20 个端口（T1..T20 / B1..B20 / L1..L20 / R1..R20），
 * 起终点可落在边的任意 1/20 位置上，同一对元件可连多条线。
 */
function makePort(id: string, spot: go.Spot, fromLinkable: boolean, toLinkable: boolean): go.Shape {
  // 根据 spot 所在边确定连线出入方向
  const side =
    spot.y === 0 ? go.Spot.Top : spot.y === 1 ? go.Spot.Bottom : spot.x === 0 ? go.Spot.Left : go.Spot.Right;
  return $(go.Shape, 'Rectangle', {
    portId: id,
    fromLinkable,
    toLinkable,
    fromSpot: side,
    toSpot: side,
    desiredSize: new go.Size(8, 8),
    fill: 'transparent',
    stroke: null,
    alignment: spot,
    // alignmentFocus 取所在边方向：端口整体收进图标边缘内侧，连线端点正好落在图标边界上
    alignmentFocus: side,
    cursor: 'crosshair',
  });
}

/** 生成四条边各 20 个端口，共 80 个 */
function makeEdgePorts(): go.Shape[] {
  const ports: go.Shape[] = [];
  const N = 20;
  for (let i = 1; i <= N; i++) {
    const f = i / N;
    ports.push(makePort(`T${i}`, new go.Spot(f, 0, 0, 0), true, true));
    ports.push(makePort(`B${i}`, new go.Spot(f, 1, 0, 0), true, true));
    ports.push(makePort(`L${i}`, new go.Spot(0, f, 0, 0), true, true));
    ports.push(makePort(`R${i}`, new go.Spot(1, f, 0, 0), true, true));
  }
  return ports;
}

/**
 * 悬浮提示：节点/连线数据含 description 字段时，
 * 显示 "名称：description"（元件取 name，连线取 text）；无 description 时不显示
 */
function createDescriptionToolTip(): go.Adornment {
  return $(
    go.Adornment,
    'Auto',
    new go.Binding('visible', 'description', (v) => typeof v === 'string' && v !== ''),
    $(go.Shape, 'RoundedRectangle', {
      fill: '#fffbe6',
      stroke: '#d9d9d9',
      strokeWidth: 0.8,
      parameter1: 3,
    }),
    $(
      go.TextBlock,
      {
        margin: 6,
        font: '16px "Microsoft YaHei", sans-serif',
        stroke: '#333',
        maxSize: new go.Size(320, NaN),
        wrap: go.TextBlock.WrapFit,
      },
      new go.Binding('text', '', (d) => {
        const data = d as { name?: string; text?: string; description?: string };
        return `${data.name ?? data.text ?? ''}：${data.description ?? ''}`;
      })
    )
  );
}

/**
 * 点位信息悬浮提示：category 为 Comment 的文本框，数据含
 * pointName / pointNo / pointDesc 字段时显示
 * "点位信息：\n pointName pointNo \n pointDesc"；三者皆空时不显示
 */
function createPointInfoToolTip(): go.Adornment {
  return $(
    go.Adornment,
    'Auto',
    new go.Binding('visible', '', (d) => {
      const data = d as { pointName?: string; pointNo?: string; pointDesc?: string };
      return !!(data.pointName || data.pointNo || data.pointDesc);
    }),
    $(go.Shape, 'RoundedRectangle', {
      fill: '#fffbe6',
      stroke: '#d9d9d9',
      strokeWidth: 0.8,
      parameter1: 3,
    }),
    $(
      go.TextBlock,
      {
        margin: 6,
        font: '16px "Microsoft YaHei", sans-serif',
        stroke: '#333',
        maxSize: new go.Size(320, NaN),
        wrap: go.TextBlock.WrapFit,
      },
      new go.Binding('text', '', (d) => {
        const data = d as { pointName?: string; pointNo?: string; pointDesc?: string };
        return `点位信息：\n${data.pointName ?? ''} ${data.pointNo ?? ''}\n${data.pointDesc ?? ''}`;
      })
    )
  );
}

/**
 * 元件节点模板：图标 + 可编辑名称。
 * 图标本身不是端口（按住可移动、可缩放、可旋转）；
 * 上下左右四个小方块是连接端口（悬停显示），拖出即连线。
 * 图标默认按 SVG 原始大小显示；传 maxIconSize 时（元件库）超出上限才等比缩小。
 * 传 onEditDesc 时（画布）带右键菜单"编辑描述"；元件库面板不传、无右键菜单。
 */
function createNodeTemplate(
  maxIconSize?: go.Size,
  showName = false,
  onEditDesc?: (nodeKey: go.Key) => void
): go.Node {
  const iconProps: Record<string, unknown> = {
    name: 'ICON',
    imageStretch: go.GraphObject.Uniform,
  };
  if (maxIconSize) iconProps.maxSize = maxIconSize;
  // 名称标签：画布中不显示（用可拖动的关联文本框代替），元件库面板中显示
  const nameLabel = showName
    ? [
        $(
          go.TextBlock,
          {
            margin: new go.Margin(4, 0, 0, 0),
            font: '12px "Microsoft YaHei", sans-serif',
            stroke: '#333',
            maxSize: new go.Size(120, NaN),
            wrap: go.TextBlock.WrapFit,
            textAlign: 'center',
          },
          new go.Binding('text', 'name')
        ),
      ]
    : [];
  return $(
    go.Node,
    'Vertical',
    {
      locationSpot: go.Spot.Center,
      locationObjectName: 'BODY',
      // 选中框只包含图标部分，不含下方名称；拖动名称依然会拖动整个元件（GoJS 默认）
      selectionObjectName: 'BODY',
      toolTip: createDescriptionToolTip(),
      ...(onEditDesc ? { contextMenu: createNodeContextMenu(onEditDesc) } : {}),
      movable: true,
      resizable: true,
      resizeObjectName: 'ICON',
      rotatable: true,
      rotateObjectName: 'BODY',
      // 旋转手柄：圆点在元件右侧 30px（RotatingTool 默认 handleAngle 0 / handleDistance 30），
      // 圆点与元件之间用一条虚线连接
      rotateAdornmentTemplate: $(
        go.Adornment,
        {
          locationSpot: go.Spot.Center,
          locationObjectName: 'HANDLE',
        },
        $(
          go.Panel,
          'Spot',
          { cursor: 'grab' },
          $(
            go.Shape,
            'Circle',
            {
              name: 'HANDLE',
              cursor: 'grab',
              width: 10,
              height: 10,
              fill: '#a5e3ff',
              stroke: '#4a90d9',
              strokeWidth: 1,
            }
          ),
          $(
            go.Shape,
            {
              // 从元件顶边连到圆点下缘的竖直虚线（长 45 = 手柄距离 50 - 圆半径 5）
              geometryString: 'M0 0 L0 45',
              isGeometryPositioned: true,
              stroke: '#4a90d9',
              strokeWidth: 1,
              strokeDashArray: [4, 3],
              alignment: go.Spot.Bottom,
              alignmentFocus: go.Spot.Top,
              pickable: false,
            }
          )
        )
      ),
      mouseEnter: (_e, obj) => {
        if (obj instanceof go.Node) setPortsVisible(obj, true);
      },
      mouseLeave: (_e, obj) => {
        if (obj instanceof go.Node) setPortsVisible(obj, false);
      },
    },
    new go.Binding('location', 'loc', go.Point.parse).makeTwoWay(go.Point.stringify),
    $(
      go.Panel,
      'Spot',
      {
        name: 'BODY',
      },
      new go.Binding('angle').makeTwoWay(),
      $(
        go.Picture,
        iconProps,
        new go.Binding('source', 'icon', (icon: string) => (icon ? iconBase + icon : '')),
        new go.Binding('desiredSize', 'size', go.Size.parse).makeTwoWay(go.Size.stringify)
      ),
      ...makeEdgePorts()
    ),
    ...nameLabel
  );
}

/** 组合文本框字体字符串 */
function composeFont(d: { fontSize?: number; bold?: boolean; italic?: boolean }): string {
  const size = d.fontSize ?? 13;
  return `${d.italic ? 'italic ' : ''}${d.bold ? 'bold ' : ''}${size}px "Microsoft YaHei", sans-serif`;
}

/** 右键菜单按钮 */
function makeContextButton(
  text: string,
  action: (e: go.InputEvent, obj: go.GraphObject) => void
): go.Panel {
  return $(
    go.Panel,
    'Auto',
    {
      click: action,
      cursor: 'pointer',
      stretch: go.GraphObject.Horizontal,
      mouseEnter: (_e, panel) => {
        const bg = (panel as go.Panel).findObject('BTNBG');
        if (bg instanceof go.Shape) bg.fill = '#eaf3fd';
      },
      mouseLeave: (_e, panel) => {
        const bg = (panel as go.Panel).findObject('BTNBG');
        if (bg instanceof go.Shape) bg.fill = '#ffffff';
      },
    },
    $(go.Shape, 'Rectangle', { name: 'BTNBG', fill: '#ffffff', stroke: null }),
    $(go.TextBlock, text, {
      margin: new go.Margin(6, 14),
      font: '12px "Microsoft YaHei", sans-serif',
      stroke: '#333',
    })
  );
}

function adornedNodeKey(obj: go.GraphObject): go.Key | null {
  const node = (obj.part as go.Adornment).adornedPart;
  return node ? (node.data.key as go.Key) : null;
}

/** 普通文本框右键菜单：关联元件 / 取消关联 / 编辑点位 / 调整样式 */
function createCommentContextMenu(
  onAssociate: (commentKey: go.Key) => void,
  onStyle: (commentKey: go.Key) => void,
  onEditPoint: (commentKey: go.Key) => void
): go.Adornment {
  return $(
    go.Adornment,
    'Vertical',
    { background: '#c9c9c9' },
    makeContextButton('关联元件', (e, obj) => {
      const key = adornedNodeKey(obj);
      if (key !== null) onAssociate(key);
    }),
    makeContextButton('取消关联', (e, obj) => {
      const node = (obj.part as go.Adornment).adornedPart;
      if (node) {
        e.diagram.model.commit((m) => m.setDataProperty(node.data, 'assocKey', undefined), 'unassociate comment');
      }
    }),
    makeContextButton('编辑点位', (e, obj) => {
      const key = adornedNodeKey(obj);
      if (key !== null) onEditPoint(key);
    }),
    makeContextButton('调整样式', (e, obj) => {
      const key = adornedNodeKey(obj);
      if (key !== null) onStyle(key);
    })
  );
}

/** 名称标签右键菜单：只有调整样式（与元件永久绑定，不提供取消关联） */
function createCommentLabelContextMenu(onStyle: (commentKey: go.Key) => void): go.Adornment {
  return $(
    go.Adornment,
    'Vertical',
    { background: '#c9c9c9' },
    makeContextButton('调整样式', (e, obj) => {
      const key = adornedNodeKey(obj);
      if (key !== null) onStyle(key);
    })
  );
}

/** 普通文本框（便签）：双击可编辑，右键可关联/取消关联元件、编辑点位、调整样式，随模型一起保存 */
function createCommentTemplate(
  onAssociate: (commentKey: go.Key) => void,
  onStyle: (commentKey: go.Key) => void,
  onEditPoint: (commentKey: go.Key) => void
): go.Node {
  return $(
    go.Node,
    'Spot',
    {
      locationSpot: go.Spot.Center,
      movable: true,
      // 可缩放：拖动底框边缘手柄改变大小，尺寸存入数据 size 字段（随 JSON 保存）
      resizable: true,
      resizeObjectName: 'BODY',
      contextMenu: createCommentContextMenu(onAssociate, onStyle, onEditPoint),
      // 含点位信息（pointName/pointNo/pointDesc）时悬浮显示
      toolTip: createPointInfoToolTip(),
      // 悬停显示连接端口（与元件一致），拖出即可连线
      mouseEnter: (_e, obj) => {
        if (obj instanceof go.Node) setPortsVisible(obj, true);
      },
      mouseLeave: (_e, obj) => {
        if (obj instanceof go.Node) setPortsVisible(obj, false);
      },
    },
    new go.Binding('location', 'loc', go.Point.parse).makeTwoWay(go.Point.stringify),
    $(
      go.Panel,
      'Auto',
      // 米黄底色，已关联的描边蓝色加粗；背景色/边框色可被样式设置覆盖
      $(
        go.Shape,
        'RoundedRectangle',
        {
          name: 'BODY',
          fill: 'rgba(255, 248, 200, 0.9)',
          stroke: '#e0cf7a',
          strokeWidth: 1,
          minSize: new go.Size(40, 24),
          // 圆角半径，可被样式设置（borderRadius）覆盖
          parameter1: 4,
        },
        new go.Binding('parameter1', 'borderRadius', (v) => (typeof v === 'number' ? v : 4)),
        new go.Binding('desiredSize', 'size', go.Size.parse).makeTwoWay(go.Size.stringify),
        new go.Binding('fill', 'bgColor', (v) => (v as string) ?? 'rgba(255, 248, 200, 0.9)'),
        new go.Binding('stroke', '', (d) => {
          const data = d as { borderColor?: string; assocKey?: go.Key };
          return data.borderColor ?? (data.assocKey !== undefined ? '#4a90d9' : '#e0cf7a');
        }),
        new go.Binding('strokeWidth', '', (d) => {
          const data = d as { borderWidth?: number; assocKey?: go.Key };
          return data.borderWidth ?? (data.assocKey !== undefined ? 1.6 : 1);
        })
      ),
      $(
        go.TextBlock,
        {
          editable: true,
          // 不换行，宽度随文字自适应（手动输入的换行符仍然生效）
          wrap: go.TextBlock.None,
          minSize: new go.Size(20, 14),
          margin: 8,
          font: '13px "Microsoft YaHei", sans-serif',
          stroke: '#5c4d00',
        },
        new go.Binding('text', 'text').makeTwoWay(),
        new go.Binding('font', '', (d) => composeFont(d as { fontSize?: number; bold?: boolean; italic?: boolean })),
        new go.Binding('stroke', 'textColor', (v) => (v as string) ?? '#5c4d00'),
        new go.Binding('margin', 'textMargin', (v) => new go.Margin(typeof v === 'number' ? v : 8))
      )
    ),
    // 上下左右四个连接端口，可引出/接入多条连线
    makePort('T', new go.Spot(0.5, 0, 0, 0), true, true),
    makePort('B', new go.Spot(0.5, 1, 0, 0), true, true),
    makePort('L', new go.Spot(0, 0.5, 0, 0), true, true),
    makePort('R', new go.Spot(1, 0.5, 0, 0), true, true)
  );
}

/** 元件名称标签：透明纯文字，右键只有"调整样式"，可单独删除，删除后元件保留原名 */
function createCommentLabelTemplate(onStyle: (commentKey: go.Key) => void): go.Node {
  return $(
    go.Node,
    'Auto',
    {
      locationSpot: go.Spot.Center,
      movable: true,
      // 可缩放：拖动底框边缘手柄改变大小，尺寸存入数据 size 字段（随 JSON 保存）
      resizable: true,
      resizeObjectName: 'BODY',
      contextMenu: createCommentLabelContextMenu(onStyle),
    },
    new go.Binding('location', 'loc', go.Point.parse).makeTwoWay(go.Point.stringify),
    $(
      go.Shape,
      'RoundedRectangle',
      {
        name: 'BODY',
        fill: 'transparent',
        stroke: 'transparent',
        strokeWidth: 1,
        minSize: new go.Size(40, 24),
        // 圆角半径，可被样式设置（borderRadius）覆盖；默认透明时视觉上与直角一致
        parameter1: 4,
      },
      new go.Binding('desiredSize', 'size', go.Size.parse).makeTwoWay(go.Size.stringify),
      new go.Binding('parameter1', 'borderRadius', (v) => (typeof v === 'number' ? v : 4)),
      new go.Binding('fill', 'bgColor', (v) => (v as string) ?? 'transparent'),
      new go.Binding('stroke', 'borderColor', (v) => (v as string) ?? 'transparent'),
      new go.Binding('strokeWidth', 'borderWidth', (v) => (typeof v === 'number' ? v : 1))
    ),
    $(
      go.TextBlock,
      {
        editable: true,
        // 不换行，宽度随文字自适应（手动输入的换行符仍然生效）
        wrap: go.TextBlock.None,
        minSize: new go.Size(20, 14),
        margin: 8,
        font: '13px "Microsoft YaHei", sans-serif',
        stroke: '#333333',
      },
      new go.Binding('text', 'text').makeTwoWay(),
      new go.Binding('font', '', (d) => composeFont(d as { fontSize?: number; bold?: boolean; italic?: boolean })),
      new go.Binding('stroke', 'textColor', (v) => (v as string) ?? '#333333'),
      new go.Binding('margin', 'textMargin', (v) => new go.Margin(typeof v === 'number' ? v : 8))
    )
  );
}

/**
 * 计算点在连线路径（折线）上的最近位置：
 * 返回该点占整条路径的长度比例（0~1）以及相对最近点的偏移。
 */
function nearestPointOnLink(link: go.Link, p: go.Point): { fraction: number; offset: go.Point } {
  const pts = link.points;
  let total = 0;
  for (let i = 0; i < pts.count - 1; i++) {
    total += Math.hypot(pts.elt(i + 1).x - pts.elt(i).x, pts.elt(i + 1).y - pts.elt(i).y);
  }
  let bestD2 = Infinity;
  let bestFraction = 0;
  let bestX = p.x;
  let bestY = p.y;
  let acc = 0;
  for (let i = 0; i < pts.count - 1; i++) {
    const a = pts.elt(i);
    const b = pts.elt(i + 1);
    const vx = b.x - a.x;
    const vy = b.y - a.y;
    const len2 = vx * vx + vy * vy;
    const segLen = Math.sqrt(len2);
    let t = len2 === 0 ? 0 : ((p.x - a.x) * vx + (p.y - a.y) * vy) / len2;
    t = Math.max(0, Math.min(1, t));
    const qx = a.x + vx * t;
    const qy = a.y + vy * t;
    const d2 = (p.x - qx) * (p.x - qx) + (p.y - qy) * (p.y - qy);
    if (d2 < bestD2) {
      bestD2 = d2;
      bestFraction = total === 0 ? 0 : (acc + segLen * t) / total;
      bestX = qx;
      bestY = qy;
    }
    acc += segLen;
  }
  return { fraction: bestFraction, offset: new go.Point(p.x - bestX, p.y - bestY) };
}

/**
 * 连接点小圆点：GoJS 连线不能直接终止在另一条连线上，
 * 所以在落点生成一个挂在目标连线上的小圆点（标签节点，随线移动），
 * 文本框用真实连线连到该圆点。圆点本身即端口，可重连、可删除。
 * zOrder 提高一层：加载 JSON 时连线在节点之后加入画布，连线的透明命中区（10px）
 * 会把圆点（8px）整个盖住，导致无法选中/拖动
 */
function createJointTemplate(): go.Node {
  return $(
    go.Node,
    {
      locationSpot: go.Spot.Center,
      // 放入 Foreground 层：连线的 10px 透明命中区在默认层，同层内 label 节点的 zOrder
      // 不影响命中顺序（实测 zOrder 无效），必须换到上层 Layer 才能被点到
      layerName: 'Foreground',
      // 本身不作为连线起点/终点：按住拖动是"沿线移动连接点"（LinkLabelOnPathDraggingTool），
      // 若为 true，按下拖动会被识别成从端口引出新连线
      fromLinkable: false,
      toLinkable: false,
    },
    new go.Binding('location', 'loc', go.Point.parse).makeTwoWay(go.Point.stringify),
    // 挂在连线上时记录在线路中的位置（路径比例 + 偏移），随 JSON 保存
    new go.Binding('segmentIndex').makeTwoWay(),
    new go.Binding('segmentFraction').makeTwoWay(),
    new go.Binding('segmentOffset', 'segmentOffset', go.Point.parse).makeTwoWay(go.Point.stringify),
    $(go.Shape, 'Circle', {
      width: 8,
      height: 8,
      fill: '#000',
      stroke: '#ffffff',
      strokeWidth: 1,
      cursor: 'crosshair',
    })
  );
}

/** 在目标连线的指定落点生成连接点，并从文本框引一条真实连线到该点 */
function connectCommentToLinkAt(
  diagram: go.Diagram,
  targetLink: go.Link,
  comment: go.Node,
  pt: go.Point,
  fromPortId?: string
): void {
  const model = diagram.model as go.GraphLinksModel;
  const { fraction } = nearestPointOnLink(targetLink, pt);
  const jointKey = `joint_${Date.now().toString(36)}_${Math.floor(Math.random() * 100000)}`;
  model.addNodeData({
    key: jointKey,
    category: 'LinkJoint',
    loc: go.Point.stringify(pt),
    segmentFraction: fraction,
    // 偏移为零：连接点精确落在连线路径上，可沿线拖动（LinkLabelOnPathDraggingTool）
    segmentOffset: '0 0',
  });
  model.addLabelKeyForLinkData(targetLink.data, jointKey);
  const linkData: Record<string, unknown> = {
    from: comment.data.key as go.Key,
    to: jointKey,
    text: nextStreamLabel(diagram),
  };
  if (fromPortId) linkData.fromPort = fromPortId;
  model.addLinkData(linkData);
}

/** 把拖动选区中的普通文本框用连线连到目标连线的最近点 */
function connectCommentsToLink(diagram: go.Diagram, link: go.Link): void {
  const nodes: go.Node[] = [];
  diagram.selection.each((part) => {
    if (part instanceof go.Node && part.category === 'Comment') {
      nodes.push(part);
    }
  });
  for (const node of nodes) {
    connectCommentToLinkAt(diagram, link, node, node.location);
  }
}

/** 查找画布某点下的真实连线（排除连线工具正在拖出的临时线） */
function findRealLinkAt(diagram: go.Diagram, pt: go.Point): go.Link | null {
  let found: go.Link | null = null;
  diagram.findObjectsAt(
    pt,
    (o) => (o.part instanceof go.Link ? o.part : null),
    (p) => {
      if (!found && p instanceof go.Link && p.data !== null) {
        found = p;
      }
      return false;
    }
  );
  return found;
}

/**
 * 自定义连线工具：正常连到端口的行为不变；
 * 从文本框引出的连线若落在另一条连线上（无有效端口），
 * 则在该落点生成连接点，并把引出的线连到连接点上。
 */
class CommentLinkingTool extends go.LinkingTool {
  doMouseUp(): void {
    const diagram = this.diagram;
    const fromNode = this.originalFromNode;
    const fromPortId = this.originalFromPort?.portId ?? undefined;
    const before = fromNode ? fromNode.linksConnected.count : 0;
    super.doMouseUp();
    if (!fromNode || fromNode.category !== 'Comment') return;
    if (fromNode.linksConnected.count !== before) return; // 已成功连到端口
    const pt = diagram.lastInput.documentPoint;
    const link = findRealLinkAt(diagram, pt);
    if (link) {
      diagram.commit(() => {
        connectCommentToLinkAt(diagram, link, fromNode, pt, fromPortId);
      }, 'connect comment to link');
    }
  }
}

/** 连线右键菜单：调整样式（线条）/ 调整标签样式（名称标签）/ 编辑描述 / 隐藏或显示标签 / 隐藏或显示箭头 */
function createLinkContextMenu(
  onStyle: (linkKey: go.Key) => void,
  onLabelStyle: (linkKey: go.Key) => void,
  onEditDesc: (linkKey: go.Key) => void
): go.Adornment {
  const setLabelHidden = (e: go.InputEvent, obj: go.GraphObject, hidden: boolean) => {
    const link = (obj.part as go.Adornment).adornedPart;
    if (link instanceof go.Link) {
      e.diagram.model.commit(
        (m) => m.setDataProperty(link.data, 'labelHidden', hidden),
        'toggle link label'
      );
      // 显式触发该连线上 labelHidden 绑定的重新求值，确保标签立即显示/隐藏
      link.updateTargetBindings('labelHidden');
    }
  };
  const isLabelHidden = (o: go.GraphObject) => {
    const link = (o.part as go.Adornment | null)?.adornedPart;
    return link instanceof go.Link && link.data.labelHidden === true;
  };
  const hideButton = makeContextButton('隐藏标签', (e, obj) => setLabelHidden(e, obj, true));
  hideButton.bind(new go.Binding('visible', '', (_v, o) => !isLabelHidden(o)).ofObject());
  const showButton = makeContextButton('显示标签', (e, obj) => setLabelHidden(e, obj, false));
  showButton.bind(new go.Binding('visible', '', (_v, o) => isLabelHidden(o)).ofObject());
  const setArrowHidden = (e: go.InputEvent, obj: go.GraphObject, hidden: boolean) => {
    const link = (obj.part as go.Adornment).adornedPart;
    if (link instanceof go.Link) {
      e.diagram.model.commit(
        (m) => m.setDataProperty(link.data, 'arrowHidden', hidden),
        'toggle link arrow'
      );
      // 显式触发该连线上 arrowHidden 绑定的重新求值，确保箭头立即显示/隐藏
      link.updateTargetBindings('arrowHidden');
    }
  };
  const isArrowHidden = (o: go.GraphObject) => {
    const link = (o.part as go.Adornment | null)?.adornedPart;
    return link instanceof go.Link && link.data.arrowHidden === true;
  };
  const hideArrowButton = makeContextButton('隐藏箭头', (e, obj) => setArrowHidden(e, obj, true));
  hideArrowButton.bind(new go.Binding('visible', '', (_v, o) => !isArrowHidden(o)).ofObject());
  const showArrowButton = makeContextButton('显示箭头', (e, obj) => setArrowHidden(e, obj, false));
  showArrowButton.bind(new go.Binding('visible', '', (_v, o) => isArrowHidden(o)).ofObject());
  return $(
    go.Adornment,
    'Vertical',
    { background: '#c9c9c9' },
    makeContextButton('调整样式', (e, obj) => {
      const key = adornedNodeKey(obj);
      if (key !== null) onStyle(key);
    }),
    makeContextButton('调整标签样式', (e, obj) => {
      const key = adornedNodeKey(obj);
      if (key !== null) onLabelStyle(key);
    }),
    makeContextButton('编辑描述', (e, obj) => {
      const key = adornedNodeKey(obj);
      if (key !== null) onEditDesc(key);
    }),
    hideButton,
    showButton,
    hideArrowButton,
    showArrowButton
  );
}

/** 元件右键菜单：编辑描述（type 为 other 的元件不弹出） */
function createNodeContextMenu(onEditDesc: (nodeKey: go.Key) => void): go.Adornment {
  return $(
    go.Adornment,
    'Vertical',
    { background: '#c9c9c9' },
    // type === 'other' 的元件不显示右键菜单
    new go.Binding('visible', 'type', (t) => t !== 'other'),
    makeContextButton('编辑描述', (e, obj) => {
      const key = adornedNodeKey(obj);
      if (key !== null) onEditDesc(key);
    })
  );
}

/** 生成下一个默认连线标签：stream + 编号（取现有 streamN 的最大编号 +1） */
function nextStreamLabel(diagram: go.Diagram): string {
  const model = diagram.model as go.GraphLinksModel;
  let max = 0;
  model.linkDataArray.forEach((ld) => {
    const t = (ld as { text?: unknown }).text;
    if (typeof t === 'string') {
      const m = /^stream(\d+)$/i.exec(t.trim());
      if (m) max = Math.max(max, parseInt(m[1], 10));
    }
  });
  return `stream${max + 1}`;
}

/**
 * 连线模板：直角折线，可重连端点、可改路径；
 * 名称标签可编辑、可沿连线拖动（LinkLabelOnPathDraggingTool，靠 _isLinkLabel 标记识别）。
 * 线型（实线/虚线及间隔）、线宽、颜色存于连线数据，右键"调整样式"修改。
 */
function createLinkTemplate(
  onStyle: (linkKey: go.Key) => void,
  onLabelStyle: (linkKey: go.Key) => void,
  onEditDesc: (linkKey: go.Key) => void
): go.Link {
  return $(
    go.Link,
    {
      routing: go.Link.Orthogonal,
      corner: 0,
      // 相交流股在交点处断开（JumpGap）：后画的线在交点留缺口，看起来像另一条线从上方跨过
      curve: go.Link.JumpGap,
      // 拖动元件时只重算两端线段，保留手动编辑好的中间折点
      adjusting: go.Link.End,
      relinkableFrom: true,
      relinkableTo: true,
      reshapable: true,
      resegmentable: true,
      toolTip: createDescriptionToolTip(),
      contextMenu: createLinkContextMenu(onStyle, onLabelStyle, onEditDesc),
      // 拖放文本框到连线上：在落点生成连接点，并引一条真实连线过去
      mouseDrop: (e, obj) => {
        if (obj instanceof go.Link) connectCommentsToLink(e.diagram, obj);
      },
    },
    new go.Binding('points').makeTwoWay(),
    // 加宽的透明命中区域，便于把文本框准确拖到连线上
    // （isPanelMain 必须设置，否则拿不到连线路径几何，会渲染成默认黑块）
    $(go.Shape, { isPanelMain: true, strokeWidth: 10, stroke: 'transparent', fill: null }),
    $(
      go.Shape,
      { isPanelMain: true, strokeWidth: 1.6, stroke: '#000' },
      new go.Binding('stroke', 'lineColor', (v) => (v as string) ?? '#000'),
      new go.Binding('strokeWidth', 'lineWidth', (v) => (typeof v === 'number' ? v : 1.6)),
      new go.Binding('strokeDashArray', '', (d) => {
        const data = d as { lineDash?: string; dashLength?: number; dashGap?: number };
        return data.lineDash === 'dash' ? [data.dashLength ?? 8, data.dashGap ?? 6] : null;
      })
    ),
    $(
      go.Shape,
      { toArrow: 'Standard', stroke: null, fill: '#000', scale: 1.2 },
      new go.Binding('fill', 'lineColor', (v) => (v as string) ?? '#000'),
      new go.Binding('scale', 'arrowSize', (v) => (typeof v === 'number' ? v : 1.2)),
      // 仅在"隐藏箭头"命令或样式窗取消勾选时隐藏
      new go.Binding('visible', 'arrowHidden', (v) => v !== true)
    ),
    $(
      go.Panel,
      'Auto',
      {
        _isLinkLabel: true,
        segmentIndex: NaN,
        segmentFraction: 0.5,
        cursor: 'move',
      },
      new go.Binding('segmentIndex').makeTwoWay(),
      new go.Binding('segmentFraction').makeTwoWay(),
      new go.Binding('segmentOffset').makeTwoWay(),
      // 仅被"隐藏标签"命令隐藏时才整体不显示；文字为空时仍保留可双击编辑的小标签
      new go.Binding('visible', 'labelHidden', (v) => v !== true),
      $(
        go.Shape,
        'RoundedRectangle',
        {
          fill: '#F0FDFF',
          stroke: '#0273B9',
          strokeWidth: 0.8,
          parameter1: 3,
        },
        // 底框只在有文字时显示（空标签只是一个可双击的小编辑点，不带底框）
        new go.Binding('visible', 'text', (t: string) => !!t),
        new go.Binding('fill', 'labelBgColor', (v) => (v as string) ?? '#F0FDFF'),
        new go.Binding('stroke', 'labelBorderColor', (v) => (v as string) ?? '#0273B9'),
        new go.Binding('strokeWidth', 'labelBorderWidth', (v) => (typeof v === 'number' ? v : 0.8))
      ),
      $(
        go.TextBlock,
        {
          name: 'LINKLABEL',
          editable: true,
          font: '11px "Microsoft YaHei", sans-serif',
          stroke: '#444',
          margin: 3,
          minSize: new go.Size(10, 12),
          // 清空标签文字时自动恢复默认值（stream + 编号）
          textEdited: (tb: go.TextBlock, oldText: string, newText: string) => {
            if (newText.trim() !== '') return;
            const link = tb.part;
            if (!(link instanceof go.Link) || !link.diagram) return;
            tb.text = /^stream\d+$/i.test(oldText.trim()) ? oldText.trim() : nextStreamLabel(link.diagram);
          },
        },
        new go.Binding('text', 'text').makeTwoWay(),
        new go.Binding('font', '', (d) => {
          const data = d as { labelFontSize?: number; labelBold?: boolean; labelItalic?: boolean };
          return composeFont({
            fontSize: data.labelFontSize ?? 11,
            bold: data.labelBold,
            italic: data.labelItalic,
          });
        }),
        new go.Binding('stroke', 'labelTextColor', (v) => (v as string) ?? '#444444'),
        new go.Binding('margin', 'labelTextMargin', (v) => new go.Margin(typeof v === 'number' ? v : 3))
      )
    )
  );
}

function addCommentNode(diagram: go.Diagram, pt: go.Point): void {
  const model = diagram.model as go.GraphLinksModel;
  model.commit((m) => {
    m.addNodeData({
      // key: `text_${Date.now().toString(36)}_${Math.floor(Math.random() * 100000)}`,
      category: 'Comment',
      text: '双击编辑文字',
      loc: go.Point.stringify(pt),
    });
  }, 'add comment');
}

export default function App() {
  // 流程 id 取自路由 /project/home/observation/:f，未指定时为 0（不加载、不可保存）
  const { f } = useParams();
  const flowId = Number(f);
  const navigate = useNavigate();
  const diagramRef = useRef<go.Diagram | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const pickingKeyRef = useRef<go.Key | null>(null);
  const [picking, setPicking] = useState(false);
  const [styleEditor, setStyleEditor] = useState<{
    key: go.Key;
    target: 'node' | 'link';
    style: TextStyleForm;
    pos: { x: number; y: number };
  } | null>(null);
  const [observedDiagram, setObservedDiagram] = useState<go.Diagram | null>(null);
  // 当前流程名称（显示在操作栏左侧）
  const [flowName, setFlowName] = useState('');
  const styleEditorRef = useRef<HTMLDivElement | null>(null);
  const [linkStyleEditor, setLinkStyleEditor] = useState<{
    key: go.Key;
    style: LinkStyleForm;
    pos: { x: number; y: number };
  } | null>(null);
  const linkStyleEditorRef = useRef<HTMLDivElement | null>(null);
  const [pointEditor, setPointEditor] = useState<{
    key: go.Key;
    form: { pointName: string; pointNo: string; pointDesc: string };
    pos: { x: number; y: number };
  } | null>(null);
  const pointEditorRef = useRef<HTMLDivElement | null>(null);
  const [descEditor, setDescEditor] = useState<{
    key: go.Key;
    description: string;
    /** 元件当前名称；null 表示非元件（连线/文本框），不显示名称修改项 */
    name: string | null;
    pos: { x: number; y: number };
  } | null>(null);
  const descEditorRef = useRef<HTMLDivElement | null>(null);

  const [messageApi, contextHolder] = message.useMessage();
  // 流程 615 操作栏下方展示的子流程快捷入口（名称按 MONITOR_ENTRY_FLOW_IDS 顺序，含 615）
  const [subFlows, setSubFlows] = useState<{ id: number; name: string }[]>([]);

  // 进入快捷入口栏涉及的流程时，拉取名称用于快捷跳转；其他流程不展示
  useEffect(() => {
    if (!MONITOR_ENTRY_FLOW_IDS.includes(flowId)) {
      setSubFlows([]);
      return;
    }
    let cancelled = false;
    flows(OBSERVATION_PROJECT_ID, { page: 1, perPage: 1000 }, userInfo?.access_token)
      .then((res) => {
        if (cancelled) return;
        const list: any[] = res.data?.data || [];
        setSubFlows(
          MONITOR_ENTRY_FLOW_IDS.map((id) => list.find((it) => it.id === id))
            .filter(Boolean)
            .map((it: any) => ({ id: it.id, name: it.name }))
        );
      })
      .catch(() => {
        if (!cancelled) setSubFlows([]);
      });
    return () => {
      cancelled = true;
    };
  }, [flowId]);

  // 画布就绪后，读取项目 / 流程保存的图数据进行展示
  useEffect(() => {
    if (!observedDiagram) return;
    const diagram = diagramRef.current;
    if (!diagram) return;
    // 未指定流程时不加载
    if (!flowId) return;
    const token = userInfo?.access_token;
    getFlow(OBSERVATION_PROJECT_ID, flowId, token)
      .then(async (res) => {
        if (res.status === 200) setFlowName(res.data?.name || '');
        if (res.status === 200 && res.data?.flow_data_url) {
          const response = await fetch(res.data.flow_data_url);
          const jsonData = await response.json();
          diagram.model = go.Model.fromJson(jsonData);
          // 首次进入页面：整张流程图缩放至可视区域内并居中（留 40px 边距）。
          // 图标 Picture 是异步加载的，加载完成前文档边界不准，
          // 所以在边界变化时重复居中，2 秒后边界稳定、移除监听
          const fitToCenter = () => {
            const bounds = diagram.documentBounds;
            if (bounds.isReal()) diagram.zoomToRect(bounds.inflate(40, 40));
          };
          const onBoundsChanged = () => fitToCenter();
          diagram.addDiagramListener('DocumentBoundsChanged', onBoundsChanged);
          fitToCenter();
          requestAnimationFrame(fitToCenter);
          setTimeout(() => {
            diagram.removeDiagramListener('DocumentBoundsChanged', onBoundsChanged);
            fitToCenter();
          }, 100);
        }
      })
      .catch(() => {
        messageApi.open({ type: 'error', content: '读取流程图失败' });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [observedDiagram, flowId]);

  /** 将悬浮窗坐标限制在窗口范围内 */
  const clampEditorPos = (el: HTMLElement | null, x: number, y: number) => {
    const w = el?.offsetWidth ?? 220;
    const h = el?.offsetHeight ?? 380;
    return {
      x: Math.min(Math.max(0, x), Math.max(0, window.innerWidth - w)),
      y: Math.min(Math.max(0, y), Math.max(0, window.innerHeight - h)),
    };
  };

  /** 取右键菜单点击处的页面坐标，作为悬浮窗弹出位置 */
  const contextClickPos = (diagram: go.Diagram) => {
    const ip = diagram.lastInput.viewPoint;
    const rect = diagram.div?.getBoundingClientRect();
    return clampEditorPos(null, (rect?.left ?? 0) + ip.x, (rect?.top ?? 0) + ip.y);
  };

  /** 打开文本样式悬浮窗，读取文本框当前样式，并在鼠标位置弹出 */
  const openStyleEditor = (key: go.Key) => {
    const diagram = diagramRef.current;
    if (!diagram) return;
    const data = diagram.model.findNodeDataForKey(key) as
      | (Partial<TextStyleForm> & { isLabel?: boolean })
      | null;
    if (!data) return;
    const pos = contextClickPos(diagram);
    setStyleEditor({
      key,
      target: 'node',
      pos,
      style: {
        fontSize: data.fontSize ?? 13,
        textColor: data.textColor ?? (data.isLabel ? '#333333' : '#5c4d00'),
        bold: !!data.bold,
        italic: !!data.italic,
        bgColor: data.bgColor ?? (data.isLabel ? '#ffffff' : '#fff8c8'),
        borderColor: data.borderColor ?? (data.isLabel ? '#333333' : '#e0cf7a'),
        borderWidth: data.borderWidth ?? 1,
        borderRadius: data.borderRadius ?? 4,
        textMargin: data.textMargin ?? 8,
      },
    });
  };

  /** 应用样式修改：写入模型（实时预览、可撤销、随 JSON 保存）。
   *  target 为 link 时写入连线数据的 label* 字段（连线名称标签样式）。 */
  const applyTextStyle = (patch: Partial<TextStyleForm>) => {
    setStyleEditor((prev) => {
      if (!prev) return prev;
      const diagram = diagramRef.current;
      if (diagram) {
        diagram.model.commit((m) => {
          const data =
            prev.target === 'link'
              ? (m as go.GraphLinksModel).findLinkDataForKey(prev.key)
              : m.findNodeDataForKey(prev.key);
          if (data) {
            for (const [k, v] of Object.entries(patch)) {
              const prop = prev.target === 'link' ? `label${k.charAt(0).toUpperCase()}${k.slice(1)}` : k;
              m.setDataProperty(data, prop, v);
            }
          }
        }, 'text style');
      }
      return { ...prev, style: { ...prev.style, ...patch } };
    });
  };

  /** 打开连线名称标签的样式悬浮窗（选项与文本框一致），在鼠标位置弹出 */
  const openLinkLabelStyleEditor = (key: go.Key) => {
    const diagram = diagramRef.current;
    if (!diagram) return;
    const data = (diagram.model as go.GraphLinksModel).findLinkDataForKey(key) as
      | Partial<Record<`label${Capitalize<string>}`, unknown>>
      | null;
    if (!data) return;
    const num = (v: unknown, def: number) => (typeof v === 'number' ? v : def);
    const str = (v: unknown, def: string) => (typeof v === 'string' ? v : def);
    setStyleEditor({
      key,
      target: 'link',
      pos: contextClickPos(diagram),
      style: {
        fontSize: num(data.labelFontSize, 11),
        textColor: str(data.labelTextColor, '#444444'),
        bold: data.labelBold === true,
        italic: data.labelItalic === true,
        bgColor: str(data.labelBgColor, '#F0FDFF'),
        borderColor: str(data.labelBorderColor, '#0273B9'),
        borderWidth: num(data.labelBorderWidth, 0.8),
        textMargin: num(data.labelTextMargin, 3),
      },
    });
  };

  /** 打开连线样式悬浮窗，读取连线当前样式，并在鼠标位置弹出 */
  const openLinkStyleEditor = (key: go.Key) => {
    const diagram = diagramRef.current;
    if (!diagram) return;
    const data = (diagram.model as go.GraphLinksModel).findLinkDataForKey(key) as
      | Partial<LinkStyleForm>
      | null;
    if (!data) return;
    setLinkStyleEditor({
      key,
      pos: contextClickPos(diagram),
      style: {
        lineDash: data.lineDash === 'dash' ? 'dash' : 'solid',
        dashLength: data.dashLength ?? 8,
        dashGap: data.dashGap ?? 6,
        lineWidth: data.lineWidth ?? 1.6,
        lineColor: data.lineColor ?? '#000',
        arrowSize: data.arrowSize ?? 1.2,
        arrowHidden: data.arrowHidden === true,
      },
    });
  };

  /** 应用连线样式修改：写入模型（实时预览、可撤销、随 JSON 保存） */
  const applyLinkStyle = (patch: Partial<LinkStyleForm>) => {
    setLinkStyleEditor((prev) => {
      if (!prev) return prev;
      const diagram = diagramRef.current;
      if (diagram) {
        diagram.model.commit((m) => {
          const data = (m as go.GraphLinksModel).findLinkDataForKey(prev.key);
          if (data) {
            for (const [k, v] of Object.entries(patch)) {
              m.setDataProperty(data, k, v);
            }
          }
        }, 'link style');
      }
      return { ...prev, style: { ...prev.style, ...patch } };
    });
  };

  /** 打开点位信息悬浮窗，读取文本框当前点位数据，并在鼠标位置弹出 */
  const openPointEditor = (key: go.Key) => {
    const diagram = diagramRef.current;
    if (!diagram) return;
    const data = diagram.model.findNodeDataForKey(key) as
      | { pointName?: string; pointNo?: string; pointDesc?: string }
      | null;
    if (!data) return;
    setPointEditor({
      key,
      pos: contextClickPos(diagram),
      form: {
        pointName: data.pointName ?? '',
        pointNo: data.pointNo ?? '',
        pointDesc: data.pointDesc ?? '',
      },
    });
  };

  /** 应用点位信息修改：写入模型（实时、可撤销、随 JSON 保存） */
  const applyPointInfo = (patch: Partial<{ pointName: string; pointNo: string; pointDesc: string }>) => {
    setPointEditor((prev) => {
      if (!prev) return prev;
      const diagram = diagramRef.current;
      if (diagram) {
        diagram.model.commit((m) => {
          const data = m.findNodeDataForKey(prev.key);
          if (data) {
            for (const [k, v] of Object.entries(patch)) {
              m.setDataProperty(data, k, v);
            }
          }
        }, 'point info');
      }
      return { ...prev, form: { ...prev.form, ...patch } };
    });
  };

  /** 打开描述悬浮窗（元件节点或连线通用），读取当前 description，并在鼠标位置弹出；
   *  元件节点（含 name 字段）额外显示名称修改项 */
  const openDescEditor = (key: go.Key) => {
    const diagram = diagramRef.current;
    if (!diagram) return;
    const model = diagram.model as go.GraphLinksModel;
    const nodeData = model.findNodeDataForKey(key) as
      | { name?: string; description?: string }
      | null;
    const data = (nodeData ?? model.findLinkDataForKey(key)) as
      | { description?: string }
      | null;
    if (!data) return;
    setDescEditor({
      key,
      pos: contextClickPos(diagram),
      description: data.description ?? '',
      name: nodeData && typeof nodeData.name === 'string' ? nodeData.name : null,
    });
  };

  /** 应用描述修改：写入模型 description 字段（实时、可撤销、随 JSON 保存），tooltip 即时更新 */
  const applyDescription = (value: string) => {
    setDescEditor((prev) => {
      if (!prev) return prev;
      const diagram = diagramRef.current;
      if (diagram) {
        diagram.model.commit((m) => {
          const gm = m as go.GraphLinksModel;
          const data = gm.findNodeDataForKey(prev.key) ?? gm.findLinkDataForKey(prev.key);
          if (data) m.setDataProperty(data, 'description', value);
        }, 'edit description');
      }
      return { ...prev, description: value };
    });
  };

  /** 应用元件名称修改：写入模型 name 字段，并同步更新关联名称文本框（CommentLabel）的显示 */
  const applyNodeName = (value: string) => {
    setDescEditor((prev) => {
      if (!prev) return prev;
      const diagram = diagramRef.current;
      if (diagram) {
        diagram.model.commit((m) => {
          const gm = m as go.GraphLinksModel;
          const data = gm.findNodeDataForKey(prev.key);
          if (data) m.setDataProperty(data, 'name', value);
          gm.nodeDataArray.forEach((nd) => {
            const label = nd as { category?: string; isLabel?: boolean; assocKey?: go.Key };
            if (
              label.category === 'CommentLabel' &&
              label.isLabel &&
              label.assocKey === prev.key
            ) {
              m.setDataProperty(nd, 'text', value);
            }
          });
        }, 'edit node name');
      }
      return { ...prev, name: value };
    });
  };

  // 悬浮窗打开后按实际尺寸重新校正一次位置，保证不超出窗口
  useEffect(() => {
    if (styleEditor) {
      const p = clampEditorPos(styleEditorRef.current, styleEditor.pos.x, styleEditor.pos.y);
      if (p.x !== styleEditor.pos.x || p.y !== styleEditor.pos.y) {
        setStyleEditor((prev) => (prev ? { ...prev, pos: p } : prev));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [styleEditor?.key]);

  useEffect(() => {
    if (linkStyleEditor) {
      const p = clampEditorPos(linkStyleEditorRef.current, linkStyleEditor.pos.x, linkStyleEditor.pos.y);
      if (p.x !== linkStyleEditor.pos.x || p.y !== linkStyleEditor.pos.y) {
        setLinkStyleEditor((prev) => (prev ? { ...prev, pos: p } : prev));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkStyleEditor?.key]);

  useEffect(() => {
    if (pointEditor) {
      const p = clampEditorPos(pointEditorRef.current, pointEditor.pos.x, pointEditor.pos.y);
      if (p.x !== pointEditor.pos.x || p.y !== pointEditor.pos.y) {
        setPointEditor((prev) => (prev ? { ...prev, pos: p } : prev));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pointEditor?.key]);

  useEffect(() => {
    if (descEditor) {
      const p = clampEditorPos(descEditorRef.current, descEditor.pos.x, descEditor.pos.y);
      if (p.x !== descEditor.pos.x || p.y !== descEditor.pos.y) {
        setDescEditor((prev) => (prev ? { ...prev, pos: p } : prev));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [descEditor?.key]);

  /** 拖动悬浮窗标题栏移动窗口，范围限制在窗口内 */
  const startEditorDrag = (
    e: React.MouseEvent,
    el: HTMLElement | null,
    startPos: { x: number; y: number },
    applyPos: (p: { x: number; y: number }) => void
  ) => {
    if (!el || (e.target as HTMLElement).closest('button')) return;
    const dx = e.clientX - startPos.x;
    const dy = e.clientY - startPos.y;
    const onMove = (ev: MouseEvent) => {
      applyPos(clampEditorPos(el, ev.clientX - dx, ev.clientY - dy));
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    e.preventDefault();
  };

  const onEditorHeaderMouseDown = (e: React.MouseEvent) => {
    if (!styleEditor) return;
    startEditorDrag(e, styleEditorRef.current, styleEditor.pos, (p) =>
      setStyleEditor((prev) => (prev ? { ...prev, pos: p } : prev))
    );
  };

  const onLinkEditorHeaderMouseDown = (e: React.MouseEvent) => {
    if (!linkStyleEditor) return;
    startEditorDrag(e, linkStyleEditorRef.current, linkStyleEditor.pos, (p) =>
      setLinkStyleEditor((prev) => (prev ? { ...prev, pos: p } : prev))
    );
  };

  const onPointEditorHeaderMouseDown = (e: React.MouseEvent) => {
    if (!pointEditor) return;
    startEditorDrag(e, pointEditorRef.current, pointEditor.pos, (p) =>
      setPointEditor((prev) => (prev ? { ...prev, pos: p } : prev))
    );
  };

  const onDescEditorHeaderMouseDown = (e: React.MouseEvent) => {
    if (!descEditor) return;
    startEditorDrag(e, descEditorRef.current, descEditor.pos, (p) =>
      setDescEditor((prev) => (prev ? { ...prev, pos: p } : prev))
    );
  };

  // 点击悬浮窗以外的任意位置（画布、菜单、工具栏等）时自动关闭样式悬浮窗
  useEffect(() => {
    const onPointerDown = (e: PointerEvent) => {
      const t = e.target as Node;
      if (
        styleEditorRef.current?.contains(t) ||
        linkStyleEditorRef.current?.contains(t) ||
        pointEditorRef.current?.contains(t) ||
        descEditorRef.current?.contains(t)
      )
        return;
      setStyleEditor(null);
      setLinkStyleEditor(null);
      setPointEditor(null);
      setDescEditor(null);
    };
    window.addEventListener('pointerdown', onPointerDown, true);
    return () => window.removeEventListener('pointerdown', onPointerDown, true);
  }, []);

  const [zoom, setZoom] = useState<number>(100);
  const [activeType, setActiveType] = useState<string>(categories[0].type);
  const [readOnly, setReadOnly] = useState(false);
  const [pointDrawerOpen, setPointDrawerOpen] = useState(false);
  const [pointList, setPointList] = useState<PointInfo[]>([]);

  /** 收集图中所有带点位信息的文本框，打开抽屉集中展示 */
  const openPointDrawer = () => {
    const diagram = diagramRef.current;
    const nodes = (diagram?.model as go.GraphLinksModel | null)?.nodeDataArray ?? [];
    setPointList(
      nodes
        .filter((d: any) => d.pointName || d.pointNo || d.pointDesc)
        .map((d: any, index: number) => ({
          key: String(d.key ?? index),
          pointName: d.pointName ?? '',
          pointNo: d.pointNo ?? '',
          pointDesc: d.pointDesc ?? '',
        }))
    );
    setPointDrawerOpen(true);
  };

  /** 切换只读/编辑模式：只读时禁止一切编辑操作（含右键菜单），并关闭打开的样式悬浮窗 */
  const toggleReadOnly = () => {
    const next = !readOnly;
    setReadOnly(next);
    const diagram = diagramRef.current;
    if (diagram) {
      diagram.isReadOnly = next;
      diagram.toolManager.contextMenuTool.isEnabled = !next;
    }
    setStyleEditor(null);
    setLinkStyleEditor(null);
    setPointEditor(null);
    setDescEditor(null);
  };

  const activeCategory = categories.find((c) => c.type === activeType) ?? categories[0];

  const paletteNodes = activeCategory.children.map((child) => ({
    // key: `${activeCategory.type}_${child.icon}`,
    name: child.name,
    icon: child.icon,
    type: child.type,
  }));

  const initDiagram = (): go.Diagram => {
    const diagram = $(go.Diagram, {
      'undoManager.isEnabled': true,
      'animationManager.isEnabled': false,
      allowDrop: true,
      allowDelete: true,
      scrollMode: go.Diagram.InfiniteScroll,
      'toolManager.mouseWheelBehavior': go.ToolManager.WheelZoom,
      model: $(go.GraphLinksModel, {
        linkKeyProperty: 'key',
        linkFromPortIdProperty: 'fromPort',
        linkToPortIdProperty: 'toPort',
        // 记录挂在连线上的文本框（标签节点）的 key，随 JSON 保存/导入
        linkLabelKeysProperty: 'labelKeys',
      }),
    });

    // 拖动元件时显示对齐参考线（GuidedDraggingTool.js 提供）
    diagram.toolManager.draggingTool = new GuidedDraggingTool();
    // 旋转手柄距离元件边缘 50px（与旋转装饰中虚线长度 45 配套：50 - 圆半径 5）
    diagram.toolManager.rotatingTool.handleDistance = 50;
    // 旋转手柄默认出现在元件顶部（0=右、90=下、180=左、270=上）
    diagram.toolManager.rotatingTool.handleAngle = 270;
    // 拖动连线名称标签（标签始终吸附在连线路径上）——官方要求作为 mouse-move 工具插到最前
    diagram.toolManager.mouseMoveTools.insertAt(0, new LinkLabelOnPathDraggingTool());
    // 允许从端口任意方向拉出连线；文本框引出的线落在连线上时吸附到该点
    diagram.toolManager.linkingTool = new CommentLinkingTool();
    diagram.toolManager.linkingTool.direction = go.LinkingTool.Either;
    // 悬浮提示响应时间：默认 850ms 改为 100ms
    diagram.toolManager.hoverDelay = 100;

    diagram.nodeTemplate = createNodeTemplate(undefined, false, openDescEditor);
    diagram.nodeTemplateMap.add(
      'Comment',
      createCommentTemplate((key) => {
        // 右键"关联元件"：进入拾取模式，接下来点击的元件即为关联目标
        pickingKeyRef.current = key;
        setPicking(true);
        diagram.currentCursor = 'crosshair';
      }, openStyleEditor, openPointEditor)
    );
    // 元件名称标签模板（右键只有"调整样式"，可单独删除，不影响元件本身）
    diagram.nodeTemplateMap.add('CommentLabel', createCommentLabelTemplate(openStyleEditor));
    // 连线上的连接点小圆点（文本框连线到 link 的落点）
    diagram.nodeTemplateMap.add('LinkJoint', createJointTemplate());
    diagram.linkTemplate = createLinkTemplate(openLinkStyleEditor, openLinkLabelStyleEditor, openDescEditor);

    const isCommentCategory = (c?: string) => c === 'Comment' || c === 'CommentLabel';

    const endPicking = () => {
      pickingKeyRef.current = null;
      setPicking(false);
      diagram.currentCursor = '';
    };

    // 拾取模式下点击元件 → 建立文本框与元件的关联
    diagram.addDiagramListener('ObjectSingleClicked', (e) => {
      if (pickingKeyRef.current === null) return;
      const part = e.subject.part;
      if (part instanceof go.Node && !isCommentCategory(part.category)) {
        const commentKey = pickingKeyRef.current;
        diagram.model.commit((m) => {
          const commentData = m.findNodeDataForKey(commentKey);
          if (commentData) m.setDataProperty(commentData, 'assocKey', part.data.key);
        }, 'associate comment');
      }
      endPicking();
    });

    // 点击空白处取消拾取
    diagram.addDiagramListener('BackgroundSingleClicked', () => {
      if (pickingKeyRef.current !== null) endPicking();
    });

    // 新建连线自动命名默认标签：stream + 编号
    diagram.addDiagramListener('LinkDrawn', (e) => {
      const link = e.subject;
      if (link instanceof go.Link && link.data && !link.data.text) {
        diagram.model.commit((m) => m.setDataProperty(link.data, 'text', nextStreamLabel(diagram)), 'default link label');
      }
    });

    // 元件拖入画布后，自动在其下方创建一个已关联的名称文本框（可随意拖动位置）
    diagram.addDiagramListener('ExternalObjectsDropped', (e) => {
      const model = e.diagram.model as go.GraphLinksModel;
      e.subject.each((part: go.Part) => {
        if (part instanceof go.Node && !isCommentCategory(part.category)) {
          const b = part.actualBounds;
          model.addNodeData({
            // key: `label_${Date.now().toString(36)}_${Math.floor(Math.random() * 100000)}`,
            category: 'CommentLabel',
            isLabel: true,
            assocKey: part.data.key,
            text: part.data.name ?? '',
            loc: go.Point.stringify(new go.Point(b.x + b.width / 2, b.y + b.height + 14)),
          });
        }
      });
    });

    // 元件位置变化时，同步平移所有关联到它的文本框；删除元件时清理失效关联
    diagram.addModelChangedListener((e) => {
      const model = e.model as go.GraphLinksModel;
      if (e.change === go.ChangedEvent.Property && e.propertyName === 'loc' && e.object) {
        const data = e.object as { key: go.Key; category?: string };
        if (isCommentCategory(data.category)) return;
        const oldLoc = go.Point.parse(typeof e.oldValue === 'string' ? e.oldValue : '0 0');
        const newLoc = go.Point.parse(typeof e.newValue === 'string' ? e.newValue : '0 0');
        const dx = newLoc.x - oldLoc.x;
        const dy = newLoc.y - oldLoc.y;
        if (dx === 0 && dy === 0) return;
        model.nodeDataArray.forEach((nd) => {
          const follower = nd as { category?: string; assocKey?: go.Key; loc?: string };
          if (isCommentCategory(follower.category) && follower.assocKey === data.key) {
            // 该文本框正随选中集一起被拖动时跳过，避免双重移动
            const followerNode = diagram.findNodeForData(nd);
            if (followerNode && followerNode.isSelected) return;
            const p = go.Point.parse(follower.loc ?? '0 0');
            model.setDataProperty(nd, 'loc', go.Point.stringify(new go.Point(p.x + dx, p.y + dy)));
          }
        });
      } else if (e.change === go.ChangedEvent.Property && e.propertyName === 'text' && e.object) {
        // 名称文本框内容修改后，同步回元件数据的 name 字段
        const data = e.object as { category?: string; isLabel?: boolean; assocKey?: go.Key };
        if (isCommentCategory(data.category) && data.isLabel && data.assocKey !== undefined) {
          const target = model.findNodeDataForKey(data.assocKey);
          if (target) model.setDataProperty(target, 'name', e.newValue);
        }
      } else if (e.change === go.ChangedEvent.Remove && e.propertyName === 'nodeDataArray' && e.oldValue) {
        const removedKey = (e.oldValue as { key: go.Key }).key;
        const labelsToRemove: go.ObjectData[] = [];
        model.nodeDataArray.forEach((nd) => {
          const follower = nd as { assocKey?: go.Key; isLabel?: boolean };
          if (follower.assocKey === removedKey) {
            if (follower.isLabel) labelsToRemove.push(nd); // 名称文本框随元件一起删除
            else model.setDataProperty(nd, 'assocKey', undefined);
          }
        });
        labelsToRemove.forEach((nd) => model.removeNodeData(nd));
      }
    });

    // 双击画布空白处，在该位置添加文本（只读模式下禁用）
    diagram.addDiagramListener('BackgroundDoubleClicked', (e) => {
      if (e.diagram.isReadOnly) return;
      addCommentNode(e.diagram, e.diagram.lastInput.documentPoint);
    });

    // 双击连线时，编辑连线名称（只读模式下禁用）
    diagram.addDiagramListener('ObjectDoubleClicked', (e) => {
      if (e.diagram.isReadOnly) return;
      const part = e.subject.part;
      if (part instanceof go.Link) {
        const label = part.findObject('LINKLABEL');
        if (label instanceof go.TextBlock) {
          diagram.commandHandler.editTextBlock(label);
        }
      }
    });

    // 同步缩放百分比显示
    diagram.addDiagramListener('ViewportBoundsChanged', (e) => {
      setZoom(Math.round(e.diagram.scale * 100));
    });

    diagramRef.current = diagram;
    setObservedDiagram(diagram);
    return diagram;
  };

  const initPalette = (): go.Palette => {
    const palette = $(go.Palette, {
      'animationManager.isEnabled': false,
      allowDelete: false,
      allowZoom: false,
      allowVerticalScroll: false,
      contentAlignment: go.Spot.LeftCenter,
      padding: new go.Margin(12, 20),
      layout: $(go.GridLayout, {
        wrappingColumn: 9999,
        spacing: new go.Size(48, 10),
        alignment: go.GridLayout.Position,
      }),
    });
    palette.toolManager.linkingTool.isEnabled = false;
    palette.nodeTemplate = createNodeTemplate(new go.Size(120, 90), true);
    return palette;
  };

  /** 保存流程图：把画布图数据提交到指定项目 / 流程 */
  const handleSave = async () => {
    const diagram = diagramRef.current;
    if (!diagram) return;
    if (!flowId) {
      messageApi.open({ type: 'error', content: '未指定流程，无法保存' });
      return;
    }
    const token = userInfo?.access_token;
    // 保存前同步更新流程封面：截取画布上传并写回 thumbnail_url（与流程设计页保存一致）
    updateFlowthum_Pic({ project_id: OBSERVATION_PROJECT_ID, id: flowId }, false);
    try {
      const res = await saveFlow(
        OBSERVATION_PROJECT_ID,
        flowId,
        JSON.parse(diagram.model.toJson()),
        token
      );
      if (res.status === 200) {
        messageApi.open({ type: 'success', content: '保存成功' });
      } else {
        messageApi.open({ type: 'error', content: `保存失败：${res.status}` });
      }
    } catch {
      messageApi.open({ type: 'error', content: '保存失败' });
    }
  };

  /** 导入流程图 JSON 文件 */
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const diagram = diagramRef.current;
      if (!diagram) return;
      try {
        const model = go.Model.fromJson(reader.result as string);
        if (!(model instanceof go.GraphLinksModel)) {
          throw new Error('not a GraphLinksModel');
        }
        diagram.model = model;
      } catch {
        window.alert('导入失败：文件格式不正确，请选择本工具导出的 JSON 文件。');
      }
    };
    reader.readAsText(file);
  };
    /** 保存流程图为 JSON 文件 */
  const handleExport = () => {
    const diagram = diagramRef.current;
    if (!diagram) return;
    const json = diagram.model.toJson();
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'flowchart.json';
    a.click();
    URL.revokeObjectURL(url);
  };

  /** 在画布视口中心添加文本 */
  const handleAddText = () => {
    const diagram = diagramRef.current;
    if (!diagram) return;
    const vb = diagram.viewportBounds;
    const pt = diagram.transformViewToDoc(new go.Point(vb.width / 2, vb.height / 2));
    addCommentNode(diagram, pt);
  };

  const handleClear = () => {
    const diagram = diagramRef.current;
    if (!diagram) return;
    if (window.confirm('确定要清空画布吗？此操作可通过 Ctrl+Z 撤销。')) {
      // 2.3.8 的 Model.clear() 不发变更事件（画布不刷新），改用事务批量删除
      diagram.model.commit((m) => {
        const gm = m as go.GraphLinksModel;
        gm.removeLinkDataCollection([...gm.linkDataArray]);
        gm.removeNodeDataCollection([...gm.nodeDataArray]);
      }, 'clear diagram');
    }
  };

  const zoomIn = () => {
    const diagram = diagramRef.current;
    if (diagram) diagram.commandHandler.increaseZoom();
  };

  const zoomOut = () => {
    const diagram = diagramRef.current;
    if (diagram) diagram.commandHandler.decreaseZoom();
  };

  const zoomToFit = () => {
    const diagram = diagramRef.current;
    if (diagram) diagram.commandHandler.zoomToFit();
  };

  const zoomReset = () => {
    const diagram = diagramRef.current;
    if (diagram) {
      diagram.scale = 1;
      diagram.commandHandler.resetZoom();
    }
  };

  return (
    <>
    <TopBar title="实时监控" mainContent={
                    <Navbar />
                  } />
    <div className="app">
      {contextHolder}
      <div className="toolbar-bar">
        <span className="toolbar-label">{flowName}</span>
        <button
          className={readOnly ? 'tb-btn tb-btn-first tb-w90 tb-btn-active' : 'tb-btn tb-btn-first tb-w90'}
          onClick={toggleReadOnly}
          title={readOnly ? '当前为只读模式，点击切换为编辑模式' : '当前为编辑模式，点击切换为只读模式'}
        >
          {readOnly ? <EyeOutlined /> : <EditOutlined />}
          {readOnly ? '只读模式' : '编辑模式'}
        </button>
        <button className="tb-btn tb-w90" onClick={handleAddText} disabled={readOnly}>
          <FontSizeOutlined />
          添加文本
        </button>
        <button className="tb-btn tb-w70" onClick={handleSave}>
          <SaveOutlined />
          保存
        </button>
        <button className="tb-btn tb-w70" onClick={() => fileInputRef.current?.click()}>
          <ImportOutlined />
          导入
        </button>
        <button className="tb-btn tb-w70" onClick={handleExport}>
          <ExportOutlined />
          导出
        </button>
        <button className="tb-btn tb-w70" onClick={handleClear} disabled={readOnly}>
          <DeleteOutlined />
          清空
        </button>
        <button className="tb-btn tb-btn-icon tb-w32" onClick={zoomOut} title="缩小">
          <ZoomOutOutlined />
        </button>
        <button className="tb-btn tb-w52" onClick={zoomReset} title="恢复 100%">
          {zoom}%
        </button>
        <button className="tb-btn tb-btn-icon tb-w32" onClick={zoomIn} title="放大">
          <ZoomInOutlined />
        </button>
        <button className="tb-btn tb-w90" onClick={zoomToFit} title="自动缩放并将全部图形居中显示">
          <ExpandOutlined />
          适应屏幕
        </button>
        <button className="tb-btn tb-w90" onClick={openPointDrawer} title="查看图中所有点位信息">
          <EnvironmentOutlined />
          点位信息
        </button>
        <input
          className="tb-file"
          type="file"
          accept="application/json,.json"
          ref={fileInputRef}
          onChange={handleFileChange}
        />
      </div>

      {subFlows.length > 0 && (
        <div className="sub-flow-bar">
          {subFlows.map((f, index) => (
            <React.Fragment key={f.id}>
              <button
                className={f.id === flowId ? 'sub-flow-btn active' : 'sub-flow-btn'}
                // 主流程 615 与其后的子流程之间留出更大间距
                style={f.id === MONITOR_ENTRY_FLOW_ID ? { marginRight: 24 } : undefined}
                onClick={() => navigate(`/project/home/observation/${f.id}`)}
              >
                {f.name}
              </button>
              {/* 子流程之间用箭头连接，呈现向右逐步推进的顺序（615 与子流程之间不加） */}
              {f.id !== MONITOR_ENTRY_FLOW_ID && index < subFlows.length - 1 && (
                <span className="sub-flow-arrow">→</span>
              )}
            </React.Fragment>
          ))}
        </div>
      )}

      <div className="canvas-area">
        {picking && <div className="pick-hint">请点击要关联的元件（点击空白处取消）</div>}
        {styleEditor && (
          <div
            className="style-editor"
            ref={styleEditorRef}
            style={{ left: styleEditor.pos.x, top: styleEditor.pos.y }}
          >
            <div className="style-editor-header" onMouseDown={onEditorHeaderMouseDown}>
              <span>{styleEditor.target === 'link' ? '标签样式' : '文本样式'}</span>
              <button className="close" onClick={() => setStyleEditor(null)}>
                ×
              </button>
            </div>
            <div className="style-editor-body">
              <label>
                <span>字号</span>
                <input
                  type="number"
                  min={8}
                  max={72}
                  value={styleEditor.style.fontSize}
                  onChange={(e) => {
                    const v = Number(e.target.value);
                    if (v > 0) applyTextStyle({ fontSize: v });
                  }}
                />
              </label>
              <label>
                <span>文字颜色</span>
                <input
                  type="color"
                  value={styleEditor.style.textColor}
                  onChange={(e) => applyTextStyle({ textColor: e.target.value })}
                />
              </label>
              <label className="inline">
                <input
                  type="checkbox"
                  checked={styleEditor.style.bold}
                  onChange={(e) => applyTextStyle({ bold: e.target.checked })}
                />
                加粗
              </label>
              <label className="inline">
                <input
                  type="checkbox"
                  checked={styleEditor.style.italic}
                  onChange={(e) => applyTextStyle({ italic: e.target.checked })}
                />
                斜体
              </label>
              <label>
                <span>背景色</span>
                <input
                  type="color"
                  value={styleEditor.style.bgColor}
                  onChange={(e) => applyTextStyle({ bgColor: e.target.value })}
                />
              </label>
              <label>
                <span>边框色</span>
                <input
                  type="color"
                  value={styleEditor.style.borderColor}
                  onChange={(e) => applyTextStyle({ borderColor: e.target.value })}
                />
              </label>
              <label>
                <span>边框宽度</span>
                <input
                  type="number"
                  min={0}
                  max={20}
                  value={styleEditor.style.borderWidth}
                  onChange={(e) => {
                    const v = Number(e.target.value);
                    if (v >= 0) applyTextStyle({ borderWidth: v });
                  }}
                />
              </label>
              {styleEditor.target === 'node' && (
                <label>
                  <span>圆角半径</span>
                  <input
                    type="number"
                    min={0}
                    max={50}
                    value={styleEditor.style.borderRadius ?? 4}
                    onChange={(e) => {
                      const v = Number(e.target.value);
                      if (v >= 0) applyTextStyle({ borderRadius: v });
                    }}
                  />
                </label>
              )}
              <label>
                <span>边距</span>
                <input
                  type="number"
                  min={0}
                  max={60}
                  value={styleEditor.style.textMargin}
                  onChange={(e) => {
                    const v = Number(e.target.value);
                    if (v >= 0) applyTextStyle({ textMargin: v });
                  }}
                />
              </label>
            </div>
          </div>
        )}
        {linkStyleEditor && (
          <div
            className="style-editor"
            ref={linkStyleEditorRef}
            style={{ left: linkStyleEditor.pos.x, top: linkStyleEditor.pos.y }}
          >
            <div className="style-editor-header" onMouseDown={onLinkEditorHeaderMouseDown}>
              <span>连线样式</span>
              <button className="close" onClick={() => setLinkStyleEditor(null)}>
                ×
              </button>
            </div>
            <div className="style-editor-body">
              <label>
                <span>线型</span>
                <select
                  value={linkStyleEditor.style.lineDash}
                  onChange={(e) => applyLinkStyle({ lineDash: e.target.value as 'solid' | 'dash' })}
                >
                  <option value="solid">实线</option>
                  <option value="dash">虚线</option>
                </select>
              </label>
              {linkStyleEditor.style.lineDash === 'dash' && (
                <>
                  <label>
                    <span>虚线长度</span>
                    <input
                      type="number"
                      min={1}
                      max={60}
                      value={linkStyleEditor.style.dashLength}
                      onChange={(e) => {
                        const v = Number(e.target.value);
                        if (v > 0) applyLinkStyle({ dashLength: v });
                      }}
                    />
                  </label>
                  <label>
                    <span>虚线间隔</span>
                    <input
                      type="number"
                      min={1}
                      max={60}
                      value={linkStyleEditor.style.dashGap}
                      onChange={(e) => {
                        const v = Number(e.target.value);
                        if (v > 0) applyLinkStyle({ dashGap: v });
                      }}
                    />
                  </label>
                </>
              )}
              <label>
                <span>线条宽度</span>
                <input
                  type="number"
                  min={0.5}
                  max={20}
                  step={0.5}
                  value={linkStyleEditor.style.lineWidth}
                  onChange={(e) => {
                    const v = Number(e.target.value);
                    if (v > 0) applyLinkStyle({ lineWidth: v });
                  }}
                />
              </label>
              <label>
                <span>线条颜色</span>
                <input
                  type="color"
                  value={linkStyleEditor.style.lineColor}
                  onChange={(e) => applyLinkStyle({ lineColor: e.target.value })}
                />
              </label>
              <label>
                <span>箭头大小</span>
                <input
                  type="number"
                  min={0.2}
                  max={10}
                  step={0.2}
                  value={linkStyleEditor.style.arrowSize}
                  onChange={(e) => {
                    const v = Number(e.target.value);
                    if (v > 0) applyLinkStyle({ arrowSize: v });
                  }}
                />
              </label>
              <label className="inline">
                <input
                  type="checkbox"
                  checked={!linkStyleEditor.style.arrowHidden}
                  onChange={(e) => applyLinkStyle({ arrowHidden: !e.target.checked })}
                />
                显示箭头
              </label>
            </div>
          </div>
        )}
        {pointEditor && (
          <div
            className="style-editor wide"
            ref={pointEditorRef}
            style={{ left: pointEditor.pos.x, top: pointEditor.pos.y }}
          >
            <div className="style-editor-header" onMouseDown={onPointEditorHeaderMouseDown}>
              <span>编辑点位</span>
              <button className="close" onClick={() => setPointEditor(null)}>
                ×
              </button>
            </div>
            <div className="style-editor-body">
              <label>
                <span>点位名称</span>
                <input
                  type="text"
                  value={pointEditor.form.pointName}
                  onChange={(e) => applyPointInfo({ pointName: e.target.value })}
                />
              </label>
              <label>
                <span>点位号</span>
                <input
                  type="text"
                  value={pointEditor.form.pointNo}
                  onChange={(e) => applyPointInfo({ pointNo: e.target.value })}
                />
              </label>
              <label>
                <span>点位描述</span>
                <textarea
                  rows={3}
                  value={pointEditor.form.pointDesc}
                  onChange={(e) => applyPointInfo({ pointDesc: e.target.value })}
                />
              </label>
            </div>
          </div>
        )}
        {descEditor && (
          <div
            className="style-editor wide"
            ref={descEditorRef}
            style={{ left: descEditor.pos.x, top: descEditor.pos.y }}
          >
            <div className="style-editor-header" onMouseDown={onDescEditorHeaderMouseDown}>
              <span>编辑描述</span>
              <button className="close" onClick={() => setDescEditor(null)}>
                ×
              </button>
            </div>
            <div className="style-editor-body">
              {descEditor.name !== null && (
                <label>
                  <span>名称</span>
                  <input
                    type="text"
                    value={descEditor.name}
                    onChange={(e) => applyNodeName(e.target.value)}
                  />
                </label>
              )}
              <label>
                <span>描述</span>
                <textarea
                  rows={4}
                  value={descEditor.description}
                  onChange={(e) => applyDescription(e.target.value)}
                />
              </label>
            </div>
          </div>
        )}
        {/* id="editor"：供 updateFlowthum_Pic 截取画布生成流程封面 */}
        <div id="editor" style={{ flex: 1, minWidth: 0, display: 'flex', position: 'relative' }}>
          <ReactDiagram
            initDiagram={initDiagram}
            divClassName="diagram"
            nodeDataArray={[]}
            linkDataArray={[]}
            skipsDiagramUpdate={true}
          />
        </div>
        {observedDiagram && (
          <ReactOverview
            divClassName="overview"
            style={{
              position: 'absolute',
              top: 10,
              right: 10,
              width: 190,
              height: 130,
              zIndex: 10,
              border: '1px solid #b9c2cc',
              background: 'rgba(255, 255, 255, 0.9)',
              boxShadow: '0 2px 6px rgba(0, 0, 0, 0.15)',
            }}
            observedDiagram={observedDiagram}
          />
        )}
      </div>

      {!readOnly && (
        <footer className="palette-bar">
          <div className="category-tabs">
            {categories.map((cat) => (
              <button
                key={cat.type}
                className={cat.type === activeType ? 'tab active' : 'tab'}
                onClick={() => setActiveType(cat.type)}
              >
                {cat.name}
              </button>
            ))}
          </div>
          <ReactPalette
            initPalette={initPalette}
            divClassName="palette"
            nodeDataArray={paletteNodes}
          />
        </footer>
      )}

      <PointInfoDrawer
        open={pointDrawerOpen}
        points={pointList}
        onClose={() => setPointDrawerOpen(false)}
      />
    </div>
    </>
    
  );
}
